import Link from "next/link"
import Image from "next/image"
import { getProxiedStoragePath } from "@/lib/image-url"
import { ShopByBrandBlockProps } from "./types"

/**
 * FRIDGE-1421: on the directory (`collectionSlug === 'all'`) a tile links the
 * brand's live collection as /api/brands decided it, never a guessed
 * `/collections/<brand slug>`; Sub-Zero's answered 404. Inside a collection the
 * brand-by-collection slug is unchanged.
 */
function brandHref(brand: ShopByBrandBlockProps['brands'][number], collectionSlug: string): string | null {
  if (collectionSlug !== 'all') return `/collections/${brand.slug}-${collectionSlug}`
  return brand.collection_href ?? null
}

export function ShopByBrandBlock({ collectionName, collectionSlug, brands: allBrands }: ShopByBrandBlockProps) {
  const brands = (allBrands ?? []).filter((b) => brandHref(b, collectionSlug) !== null)
  if (brands.length === 0) {
    return null
  }

  return (
    <div className="mt-6 px-4 sm:px-0">
      <div className="bg-white border border-gray-200 rounded-lg p-3 sm:p-4">
        <h2 className="text-lg sm:text-xl font-bold text-gray-900 mb-3">Shop {collectionName} By Brand</h2>
        
        {/* Desktop: 8 per row, compact */}
        <div className="hidden sm:grid sm:grid-cols-4 md:grid-cols-8 gap-1.5">
          {brands.map((brand) => (
            <Link
              key={brand.id}
              href={brandHref(brand, collectionSlug) as string}
              className="text-center group"
            >
              <div className="bg-white border border-gray-200 rounded-md h-[72px] flex items-center justify-center px-2 group-hover:shadow-sm group-hover:border-[#ff9951] transition-all">
                {brand.logo_path ? (
                  <Image
                    src={getProxiedStoragePath(brand.logo_path)}
                    alt={`${brand.name} logo | Fridge.com`}
                    width={100}
                    height={40}
                    className="max-h-12 w-auto object-contain"
                  />
                ) : (
                  <span className="text-gray-900 font-semibold text-xs">{brand.name}</span>
                )}
              </div>
              <div className="text-[10px] font-medium text-gray-600 mt-0.5 group-hover:text-[#0469f0] transition-colors line-clamp-1">{brand.name}</div>
            </Link>
          ))}
        </div>

        {/* Mobile: TWO rows, horizontal scroll */}
        <div className="sm:hidden -mx-4 px-4">
          <div className="overflow-x-auto pb-2">
            <div className="inline-grid grid-rows-2 grid-flow-col auto-cols-max gap-x-1.5 gap-y-1.5">
              {brands.map((brand) => (
                <Link
                  key={brand.id}
                  href={brandHref(brand, collectionSlug) as string}
                  className="text-center group w-[100px]"
                >
                  <div className="bg-white border border-gray-200 rounded h-10 flex items-center justify-center px-1.5 group-hover:shadow-sm group-hover:border-[#ff9951] transition-all">
                    {brand.logo_path ? (
                      <Image
                        src={getProxiedStoragePath(brand.logo_path)}
                        alt={`${brand.name} logo | Fridge.com`}
                        width={72}
                        height={28}
                        className="max-h-6 w-auto object-contain"
                      />
                    ) : (
                      <span className="text-gray-900 font-semibold text-[10px]">{brand.name}</span>
                    )}
                  </div>
                  <div className="text-[9px] font-medium text-gray-600 mt-0.5 group-hover:text-[#0469f0] transition-colors line-clamp-1">{brand.name}</div>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
